/**
 * KeyRemapEditor — the desktop key bindings for the merged (single-seat)
 * cluster. Each semantic signal shows its current keycaps (defaults from
 * `@djgg/controls`, or the creation's saved remap); "change" arms a capture
 * that binds the next physical key press. Validation is the same
 * `validateKeyBindings` the server enforces, surfaced inline. A remap applies
 * LIVE (proxy-injected) — no rebuild.
 */
import { useEffect, useState } from 'react';
import {
  DEFAULT_CLUSTERS,
  SEMANTIC_KEYS,
  keyCodeLabel,
  validateKeyBindings,
  type KeyBindings,
  type SemanticKey,
} from '@djgg/controls';

const SIGNAL_LABELS: Record<SemanticKey, string> = {
  up: 'Up',
  down: 'Down',
  left: 'Left',
  right: 'Right',
  action: 'Action',
};

export interface KeyRemapEditorProps {
  /** The stored sparse remap (undefined = all defaults). */
  value: Partial<KeyBindings> | undefined;
  /** Emits the next sparse remap; undefined when everything is back to default. */
  onChange: (next: Partial<KeyBindings> | undefined) => void;
  disabled?: boolean;
}

export function KeyRemapEditor({ value, onChange, disabled }: KeyRemapEditorProps) {
  const [arming, setArming] = useState<SemanticKey | null>(null);
  const problems = validateKeyBindings(value ?? {});

  useEffect(() => {
    if (!arming) return;
    const onKey = (e: KeyboardEvent): void => {
      e.preventDefault();
      setArming(null);
      if (e.code === 'Escape') return; // cancel (and Escape is reserved anyway)
      const next: Record<string, readonly string[]> = { ...(value ?? {}) };
      next[arming] = [e.code];
      onChange(next as Partial<KeyBindings>);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [arming, value, onChange]);

  function reset(signal: SemanticKey): void {
    const next: Record<string, readonly string[]> = { ...(value ?? {}) };
    delete next[signal];
    onChange(Object.keys(next).length > 0 ? (next as Partial<KeyBindings>) : undefined);
  }

  return (
    <div className="field" data-testid="key-remap">
      <label className="field__label">Keyboard keys</label>
      <p className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
        Desktop bindings for the single-player seat. Applies on the next game load — no rebuild.
      </p>
      {SEMANTIC_KEYS.map((signal) => {
        const overridden = value?.[signal] !== undefined;
        const codes = value?.[signal] ?? DEFAULT_CLUSTERS.merged[signal];
        return (
          <div className="key-remap__row" key={signal} data-testid={`key-remap-${signal}`}>
            <span className="key-remap__signal">{SIGNAL_LABELS[signal]}</span>
            <span className="key-remap__caps">
              {codes.map((code, i) => (
                <kbd className="key-remap__cap" key={`${code}-${i}`}>
                  {keyCodeLabel(code)}
                </kbd>
              ))}
            </span>
            {arming === signal ? (
              <span className="key-remap__arming" data-testid={`key-remap-arming-${signal}`}>
                press a key… (Esc cancels)
              </span>
            ) : (
              <button
                type="button"
                className="btn btn--ghost key-remap__btn"
                disabled={disabled}
                onClick={() => setArming(signal)}
                data-testid={`key-remap-change-${signal}`}
              >
                change
              </button>
            )}
            {overridden && arming !== signal && (
              <button
                type="button"
                className="btn btn--ghost key-remap__btn"
                disabled={disabled}
                onClick={() => reset(signal)}
                data-testid={`key-remap-reset-${signal}`}
              >
                reset
              </button>
            )}
          </div>
        );
      })}
      {problems.length > 0 && (
        <div className="error-text" data-testid="key-remap-error">
          {problems.join('; ')}
        </div>
      )}
    </div>
  );
}
